import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { adaptPayLetterPaymentProps } from './PayLetterPayment.adapter';
import { PayLetterPaymentProps } from './PayLetterPayment.types';
import { 
  selectUser, 
  selectIsUserLoggedIn, 
  selectIsAdminLoggedIn,
  registerCheckout,
  fetchWithcookieData,
  fetchCartItems,
  selectCartState,
  selectSelectedAddress,
  selectWithcookieData,
  searchAddress,
  selectAddress,
  selectAddressResults,
  selectAddressLoading
} from '../../../redux/WithcookieSlice';
import AddressSearchPopup from '../../common/AddressSearchPopup';
import { PriceDisplay } from '../../common/PriceDisplay';
import { useTranslation } from '../../../redux/TranslationHelper';
import './PayLetterPayment.css';

// PG 코드 타입 정의
interface PGCode {
  code: string;
  name: string;
}

// PG 코드 정의 (PayLetter 공식 지원 결제 수단)
const PG_CODES: PGCode[] = [
  { code: 'creditcard', name: '신용카드' },
  { code: 'banktransfer', name: '무통장입금' },
  // { code: 'virtualaccount', name: '가상계좌' },
  // { code: 'mobile', name: '휴대폰' },
  // { code: 'voucher', name: '문화상품권' },
  // { code: 'book', name: '도서문화상품권' },
  // { code: 'culture', name: '컬쳐랜드상품권' },
  // { code: 'smartculture', name: '스마트문상' },
  // { code: 'happymoney', name: '해피머니상품권' },
  // { code: 'mobilepop', name: '모바일팝' },
  // { code: 'teencash', name: '틴캐시' },
  // { code: 'tmoney', name: '교통카드결제' },
  // { code: 'cvs', name: '편의점캐시' },
  // { code: 'eggmoney', name: '에그머니' },
  // { code: 'oncash', name: '온캐시' },
  // { code: 'phonebill', name: '폰빌' },
  // { code: 'cashbee', name: '이즐' },
  // { code: 'kakaopay', name: '카카오페이' },
  // { code: 'payco', name: '페이코' },
  // { code: 'checkpay', name: '체크페이' },
  // { code: 'toss', name: '토스' },
  // { code: 'ssgpay', name: 'SSG페이' },
  // { code: 'naverpay', name: '네이버페이' },
  // { code: 'samsungpay', name: '삼성페이' },
  // { code: 'applepay', name: '애플페이' }
];

// 폼 데이터 타입 정의
interface FormData {
  name: string;
  phone: string;
  memo: string;
  zipcode: string;
  address: string;
  addressDetail: string;
  paymentMethod: string;
  agreeTerms: boolean;
}

interface CartItem {
  id: number;
  productId: number;
  variantId?: number | null;
  variant?: {
    id: number;
    productId: number;
    sku: string;
    barcode?: string | null;
    additionalPrice: string;
    additionalPv: string;
    stock: number;
    options?: Array<{
      variantId: number;
      optionValueId: number;
      optionValue: {
        id: number;
        value: string;
        optionGroup: {
          id: number;
          name: string;
        };
      };
    }>;
  } | null;
  options?: Record<string, any> | null;
  product_id?: number;
  product?: {
    id?: number;
    product_id?: number;
    product_name?: string;
    title?: string;
    name?: string;
    config?: {
      default_price?: number;
      discounted_price?: number;
      img_url?: string;
    };
    optionGroups?: Array<{
      id: number;
      name: string;
      code: string;
      optionValues?: Array<{
        id: number;
        value: string;
        code: string;
      }>;
    }>;
  };
  count: number;
}

/**
 * PayLetter 결제 컴포넌트
 * Payment 컴포넌트와 동일한 UI로 PayLetter 결제 처리
 */
const PayLetterPayment: React.FC<PayLetterPaymentProps> = (props) => {
  // 속성 어댑터를 통해 props 변환
  const adaptedProps = adaptPayLetterPaymentProps(props);
  const { t } = useTranslation();
  
  // 컴포넌트 속성 추출
  const { 
    className = "payment-component-wrapper padding-tb-40",
    style = {},
    data = {}
  } = adaptedProps;
  
  const {
    title = t("결제하기"),
    emptyText = t("장바구니가 비어 있습니다."),
    shippingFee = 0 // 기본 배송비
  } = data;
  
  // 에디터 모드 확인
  const isEditor = (props as any).isEditor || (props as any).isPreview;
  
  // Redux dispatch
  const dispatch = useDispatch() as any;
  
  // Redux state
  const cartState = useSelector(selectCartState);
  const cartItems = cartState.cartItems || [];
  const loading = cartState.loading;
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const selectedAddress = useSelector(selectSelectedAddress);
  const user = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  const addressResults = useSelector(selectAddressResults);
  const addressLoading = useSelector(selectAddressLoading);
  
  // 상태 정의
  const [selectedPgCode, setSelectedPgCode] = useState<string>('creditcard');
  const [showAddressPopup, setShowAddressPopup] = useState<boolean>(false);
  const [formData, setFormData] = useState<FormData>({
    name: user?.name || '',
    phone: user?.phone || '',
    memo: '',
    zipcode: '',
    address: '',
    addressDetail: '',
    paymentMethod: 'CARD',
    agreeTerms: false
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [backgroundSearchTriggered, setBackgroundSearchTriggered] = useState(false);
  
  // 회사 설정에서 무료배송 기준금액 가져오기
  const freeShippingLimit = useMemo(() => {
    try {
      let companySettingJson = null;
      
      if (withcookieData?.company?.companySettingJson) {
        companySettingJson = withcookieData.company.companySettingJson;
      } else if (withcookieData?.skin?.company?.companySettingJson) {
        companySettingJson = withcookieData.skin.company.companySettingJson;
      }
      
      if (typeof companySettingJson === 'string') {
        const parsedSettings = JSON.parse(companySettingJson);
        return parsedSettings.free_shipping_limit || 50000;
      } else if (companySettingJson && typeof companySettingJson === 'object') {
        return companySettingJson.free_shipping_limit || 50000;
      }
      
      return 50000;
    } catch (error) {
      console.error(t('무료배송 기준금액 가져오기 오류:'), error);
      return 50000;
    }
  }, [withcookieData, t]);
  
  // 선택된 주소가 변경되면 폼 데이터 업데이트
  useEffect(() => {
    if (selectedAddress) {
      setFormData(prev => ({
        ...prev,
        address: selectedAddress.road || selectedAddress.jibun || '',
        zipcode: selectedAddress.postalCode || ''
      }));
    }
  }, [selectedAddress]);
  
  // 사용자 정보가 있으면 폼에 자동 입력 (백그라운드 주소 검색 포함)
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        name: user.name || prev.name,
        phone: user.phone || prev.phone
      }));
      
      // user.address가 있고 아직 백그라운드 검색이 트리거되지 않았으면 주소 검색 실행
      if (user.address && user.address.trim() && !backgroundSearchTriggered && !selectedAddress) {
        setBackgroundSearchTriggered(true); // 중복 실행 방지
        // 백그라운드에서 주소 검색 실행 (모달 없이)
        dispatch(searchAddress(user.address.trim()));
      }
    }
  }, [user, backgroundSearchTriggered, selectedAddress, dispatch]);
  
  // 백그라운드 주소 검색 결과 처리
  useEffect(() => {
    if (backgroundSearchTriggered && addressResults && addressResults.length > 0 && !addressLoading && !selectedAddress) {
      // 첫 번째 결과를 자동으로 선택
      const firstAddress = addressResults[0];
      dispatch(selectAddress(firstAddress));
    }
  }, [backgroundSearchTriggered, addressResults, addressLoading, selectedAddress, dispatch]);
  
  // 컴포넌트 마운트 시 장바구니 정보 로드
  useEffect(() => {
    if (isUserLoggedIn || isAdminLoggedIn) {
      dispatch(fetchCartItems());
      
      if (!withcookieData) {
        dispatch(fetchWithcookieData());
      }
    }
  }, [dispatch, isUserLoggedIn, isAdminLoggedIn, withcookieData]);
  
  // 직급별 가격 계산 함수
  const calculateLevelPrice = useCallback((basePrice: number, product: any) => {
    // 사용자 정보가 없거나 로그인하지 않은 경우
    if (!user || !isUserLoggedIn) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 사용자의 level2 ID 확인
    const userLevel2Id = user.level2;
    const userLevel2Policy = user.level2Policy;
    
    // level2가 없으면 할인 없음
    if (!userLevel2Id) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    // 상품의 optionJson에서 level2_price 확인
    let levelPrice = basePrice;
    let levelName = '직급 할인'; // 기본 직급명
    
    // level2Policy가 있으면 직급명 사용
    if (userLevel2Policy) {
      levelName = userLevel2Policy.customName || userLevel2Policy.type || '직급 할인';
    } else {
      // withcookieData에서 직급명 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === userLevel2Id);
      if (matchingPolicy) {
        levelName = matchingPolicy.customName || matchingPolicy.type || '직급 할인';
      }
    }
    
    if (product?.optionJson?.level2_price && product.optionJson.level2_price[userLevel2Id]) {
      // level2_price에 해당 레벨의 가격이 있으면 그 가격 사용
      levelPrice = Number(product.optionJson.level2_price[userLevel2Id]) || basePrice;
    } else if (userLevel2Policy?.settingJson?.supply_price_ratio) {
      // level2_price가 없으면 supply_price_ratio 사용
      const supplyPriceRatio = Number(userLevel2Policy.settingJson.supply_price_ratio) || 1;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    } else {
      // withcookieData에서 supply_price_ratio 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === userLevel2Id);
      if (matchingPolicy?.settingJson?.supply_price_ratio) {
        const supplyPriceRatio = Number(matchingPolicy.settingJson.supply_price_ratio) || 1;
        levelPrice = Math.floor(basePrice * supplyPriceRatio);
      }
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName
    };
  }, [user, isUserLoggedIn, withcookieData]);

  // 상품 가격 합계 계산
  const calculateTotalPrice = useCallback((): number => {
    return cartItems.reduce((sum: number, item: CartItem) => {
      const basePrice = item.product?.config?.discounted_price 
        ? Number(item.product.config.discounted_price) 
        : item.product?.config?.default_price 
          ? Number(item.product.config.default_price) 
          : 0;
      
      // 직급별 가격 계산
      const priceInfo = calculateLevelPrice(basePrice, item.product);
      const finalBasePrice = priceInfo.levelPrice;
      
      // Variant 추가 가격 포함
      const additionalPrice = item.variant?.additionalPrice 
        ? Number(item.variant.additionalPrice) 
        : 0;
      
      const totalPrice = finalBasePrice + additionalPrice;
      
      return sum + (totalPrice * (item.count || 1));
    }, 0);
  }, [cartItems, calculateLevelPrice]);
  
  // 총 금액 계산 (상품 가격 + 배송비)
  const calculateGrandTotal = useCallback((): number => {
    const totalPrice = calculateTotalPrice();
    const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
    return totalPrice + appliedShippingFee;
  }, [calculateTotalPrice, shippingFee, freeShippingLimit]);
  
  // 폼 입력 처리
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  }, []);
  
  // 우편번호 검색 버튼 클릭 핸들러
  const handleAddressSearch = useCallback((): void => {
    setShowAddressPopup(true);
  }, []);
  
  // PayLetter 결제 요청 함수
  const requestPayLetterPayment = async (checkoutId: number, paymentData: any) => {
    try {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      
      const requestBody = {
        checkout_id: checkoutId,
        user_id: user.id,
        company_id: withcookieData?.company?.id,
        skin_id: withcookieData?.skin?.id,
        payletter_request: {
          pgcode: paymentData.pgcode,
          user_name: paymentData.user_name,
          order_no: paymentData.order_no,
          amount: paymentData.amount,
          product_name: paymentData.product_name,
          return_url: paymentData.return_url,
          callback_url: paymentData.callback_url,
          cancel_url: paymentData.cancel_url
        }
      };
      
      const response = await fetch(`${apiBaseUrl}/api/checkouts/payletter_march_request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(requestBody)
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || '결제 요청 실패');
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      console.error('PayLetter API 요청 실패:', error);
      throw error;
    }
  };
  
  // 결제 처리
  const handlePayment = useCallback(async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    
    // 에디터 모드에서는 실제 결제를 진행하지 않음
    // if (isEditor) {
    //   alert(t('에디터 모드에서는 실제 결제가 진행되지 않습니다.'));
    //   return;
    // }
    
    // 폼 검증
    if (!formData.name || !formData.phone || !formData.address || !formData.zipcode) {
      alert(t('필수 입력 항목을 모두 입력해주세요.'));
      return;
    }
    
    if (!formData.agreeTerms) {
      alert(t('결제 및 개인정보 처리방침에 동의해주세요.'));
      return;
    }
    
    // 장바구니가 비어있는지 확인
    if (!cartItems || cartItems.length === 0) {
      alert(t('구매할 상품이 장바구니에 없습니다.'));
      return;
    }
    
    setIsLoading(true);
    setError(null);
    
    try {
      // 사용자 정보 확인
      if (!user || !user.id) {
        console.error(t("[결제 오류] 사용자 정보 없음:"), user);
        alert(t("로그인 정보를 확인할 수 없습니다. 다시 로그인해주세요."));
        return;
      }
      
      // 회사 정보가 없는 경우 새로 불러오기
      if (!withcookieData) {
        await dispatch(fetchWithcookieData());
      }
      
      // 회사 ID 가져오기
      const companyId = withcookieData?.company?.id || 
                       withcookieData?.skin?.companyId || 
                       withcookieData?.skin?.company?.id;
      
      if (!companyId) {
        console.error(t("[결제 오류] 회사 ID를 찾을 수 없습니다:"), withcookieData);
        alert(t('회사 정보를 불러올 수 없습니다. 페이지를 새로고침하거나 나중에 다시 시도해주세요.'));
        return;
      }
      
      // 주문 ID 생성
      const orderId = `ORDER_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
      
      // 상품명 생성
      const firstItem = cartItems[0]?.product?.product_name || 
                       cartItems[0]?.product?.title || 
                       t('상품');
      const orderName = cartItems.length > 1 
        ? `${t(firstItem)} ${t('외')} ${cartItems.length - 1}${t('건')}` 
        : t(firstItem);
      
      const totalPrice = calculateTotalPrice();
      const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
      const grandTotal = totalPrice + appliedShippingFee;
      
      // 장바구니 아이템 구성
      const cartItems_formatted = cartItems.map((item: CartItem) => {
        let productId = item.product_id || item.product?.id || item.product?.product_id || item.id;
        
        if (!productId) {
          throw new Error(t("상품 정보가 올바르지 않습니다."));
        }
        
        return {
          product_id: Number(productId),
          company_id: companyId,
          user_id: user.id,
          count: item.count || 1
        };
      });
      
      // 장바구니 ID 목록
      const cartIds = cartItems.map(item => item.id).filter(id => id);
      
      // PayLetter PG 코드를 결제 수단으로 매핑
      let paymentMethod = 'CARD'; // 기본값
      
      if (selectedPgCode === 'banktransfer' || selectedPgCode === 'virtualaccount') {
        paymentMethod = 'SEND'; // 계좌이체/가상계좌
      } else if (selectedPgCode === 'mobile') {
        paymentMethod = 'NONE'; // 휴대폰 결제
      } else {
        paymentMethod = 'CARD'; // 신용카드, 카카오페이, 네이버페이 등
      }
      
      // 주문 등록 데이터 준비
      const orderData = {
        user_id: user.id,
        company_id: companyId,
        skin_id: withcookieData?.skin?.id || 0,
        receiver_name: formData.name,
        receiver_phone: formData.phone,
        receiver_email: user?.email || '',
        memo: formData.memo || '',
        total_price: grandTotal,
        buy_price: grandTotal,
        parcel_price: totalPrice,
        payment_method: paymentMethod, // PAYLETTER 대신 매핑된 결제 수단 사용
        address: formData.address,
        address_detail: formData.addressDetail || '',
        postal_code: formData.zipcode,
        carts: cartItems_formatted,
        cart_ids: cartIds
      };
      
      // 주문 등록 API 호출
      const result = await dispatch(registerCheckout(orderData as any)).unwrap();
      
      if (!result?.checkout?.id) {
        throw new Error(result?.message || t('주문 등록에 실패했습니다.'));
      }
      
      const checkoutId = result.checkout.id;
      const registeredOrderId = `ORDER_${checkoutId}`;
      
      // checkoutId를 쿠키에 저장 (1시간 만료)
      const expires = new Date();
      expires.setTime(expires.getTime() + (60 * 60 * 1000)); // 1시간
      document.cookie = `payletter_checkout_id=${checkoutId}; expires=${expires.toUTCString()}; path=/`;
      
      // 무통장입금 선택시 별도 처리
      if (selectedPgCode === 'banktransfer') {
        // 계좌번호
        const accountNumber = '030-131514-01-021';
        
        // 무통장입금 안내 모달 표시
        const modalContent = `
          <div style="text-align: center; padding: 30px 20px;">
            <div style="width: 60px; height: 60px; background: #4CAF50; border-radius: 50%; display: flex; align-items: center; justify-content: center; margin: 0 auto 20px;">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            </div>
            <h2 style="margin-bottom: 10px; color: #333; font-size: 24px; font-weight: 700;">${t('주문이 완료되었습니다')}</h2>
            <p style="color: #666; margin-bottom: 30px; font-size: 16px;">${t('아래 계좌로 입금해주시면 확인 후 발송해드립니다.')}</p>
            
            <div style="background: #f8f9fa; padding: 25px; border-radius: 12px; margin-bottom: 20px; border: 1px solid #e9ecef;">
              <div style="margin-bottom: 20px; padding-bottom: 20px; border-bottom: 1px solid #e0e0e0;">
                <p style="margin-bottom: 8px; color: #666; font-size: 14px;">${t('주문번호')}</p>
                <p style="margin: 0; font-size: 18px; font-weight: 600; color: #333;">${registeredOrderId}</p>
              </div>
              
              <div style="margin-bottom: 20px;">
                <p style="margin-bottom: 8px; color: #666; font-size: 14px;">${t('입금금액')}</p>
                <p style="margin: 0; font-size: 24px; font-weight: 700; color: #ff6b6b;">₩${grandTotal.toLocaleString()}</p>
              </div>
              
              <div style="background: white; padding: 20px; border-radius: 8px; border: 1px solid #e0e0e0;">
                <p style="margin-bottom: 15px; font-size: 16px; font-weight: 600; color: #333;">${t('입금계좌 정보')}</p>
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                  <span style="color: #666; font-size: 14px;">${t('은행')}</span>
                  <span style="font-weight: 600; color: #333;">${t('기업은행')}</span>
                </div>
                <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px;">
                  <div>
                    <span style="color: #666; font-size: 14px; display: block; margin-bottom: 4px;">${t('계좌번호')}</span>
                    <span style="font-weight: 600; color: #333; font-size: 16px;" id="accountNumber">${accountNumber}</span>
                  </div>
                  <button id="copyBtn" style="background: #f5f5f5; border: 1px solid #ddd; padding: 8px 16px; border-radius: 6px; cursor: pointer; font-size: 14px; color: #333; transition: all 0.2s;">
                    <svg style="width: 16px; height: 16px; vertical-align: middle; margin-right: 4px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                  </button>
                </div>
                <div style="margin-top: 15px; padding-top: 15px; border-top: 1px solid #f0f0f0;">
                  <span style="color: #666; font-size: 14px; display: block; margin-bottom: 4px;">${t('예금주')}</span>
                  <span style="font-weight: 600; color: #333; font-size: 16px;">다나눔패션주식회사</span>
                </div>
              </div>
            </div>
            
            <div style="background: #fff3cd; padding: 15px; border-radius: 8px; margin-bottom: 25px; border: 1px solid #ffeaa7;">
              <p style="margin: 0; color: #856404; font-size: 14px; line-height: 1.5;">
                <svg style="width: 16px; height: 16px; vertical-align: middle; margin-right: 6px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
                ${t('입금 시 입금자명을 꼭')} "<strong>${formData.name}</strong>"${t('으로 입금해주세요.')}
              </p>
            </div>
          </div>
        `;
        
        // 모달 생성 및 표시
        const modalDiv = document.createElement('div');
        modalDiv.innerHTML = `
          <div style="position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,0.6); z-index: 9999; display: flex; align-items: center; justify-content: center; padding: 20px;">
            <div style="background: white; border-radius: 16px; max-width: 500px; width: 100%; box-shadow: 0 20px 60px rgba(0,0,0,0.15);">
              ${modalContent}
              <div style="padding: 20px 30px 30px; text-align: center;">
                <button id="confirmBtn" style="background: #333; color: white; border: none; padding: 12px 40px; border-radius: 8px; cursor: pointer; font-size: 16px; font-weight: 600; transition: background 0.2s;">
                  ${t('확인')}
                </button>
              </div>
            </div>
          </div>
        `;
        
        document.body.appendChild(modalDiv);
        
        // 복사 버튼 클릭 이벤트
        document.getElementById('copyBtn')?.addEventListener('click', async () => {
          try {
            await navigator.clipboard.writeText(accountNumber);
            const copyBtn = document.getElementById('copyBtn');
            if (copyBtn) {
              const originalText = copyBtn.innerHTML;
              copyBtn.innerHTML = `
                <svg style="width: 16px; height: 16px; vertical-align: middle; margin-right: 4px;" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <polyline points="20 6 9 17 4 12"></polyline>
                </svg>
                ${t('복사됨!')}
              `;
              copyBtn.style.background = '#e8f5e9';
              copyBtn.style.borderColor = '#4CAF50';
              copyBtn.style.color = '#4CAF50';
              
              setTimeout(() => {
                copyBtn.innerHTML = originalText;
                copyBtn.style.background = '#f5f5f5';
                copyBtn.style.borderColor = '#ddd';
                copyBtn.style.color = '#333';
              }, 2000);
            }
          } catch (err) {
            console.error('복사 실패:', err);
          }
        });
        
        // 확인 버튼 호버 효과
        const confirmBtn = document.getElementById('confirmBtn');
        if (confirmBtn) {
          confirmBtn.addEventListener('mouseenter', () => {
            confirmBtn.style.background = '#555';
          });
          confirmBtn.addEventListener('mouseleave', () => {
            confirmBtn.style.background = '#333';
          });
        }
        
        // 확인 버튼 클릭 이벤트
        document.getElementById('confirmBtn')?.addEventListener('click', () => {
          document.body.removeChild(modalDiv);
          // 주문 완료 페이지로 이동
          window.location.href = `/order-complete?orderId=${registeredOrderId}`;
        });
        
        return;
      }
      
      // 카드 결제인 경우 PayLetter 진행
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      
      // apiBaseUrl이 비어있거나 상대 경로인 경우 현재 도메인 사용
      const absoluteApiUrl = apiBaseUrl 
        ? (apiBaseUrl.startsWith('http') ? apiBaseUrl : `${window.location.origin}${apiBaseUrl}`)
        : window.location.origin;
      
      const payletterRequestData = {
        pgcode: selectedPgCode,
        user_name: formData.name,
        order_no: registeredOrderId,
        amount: grandTotal,
        product_name: orderName,
        return_url: `${absoluteApiUrl}/payment/payletter/return`,    
        callback_url: `${absoluteApiUrl}/api/checkouts/payletter/callback`, // 백엔드 API (서버 간 통신)
        cancel_url: `${window.location.origin}/payment-fail`
      };
      
      // PayLetter API 호출
      const response = await requestPayLetterPayment(checkoutId, payletterRequestData);
      
      // API 응답에서 직접 토큰과 URL 확인
      // 서버에서 반환하는 구조에 따라 처리
      let token, online_url, mobile_url;
      
      // response가 직접 PayLetter 응답인 경우
      if (response?.token) {
        token = response.token;
        online_url = response.online_url;
        mobile_url = response.mobile_url;
      } 
      // response.data에 PayLetter 응답이 있는 경우
      else if (response?.data?.token) {
        token = response.data.token;
        online_url = response.data.online_url;
        mobile_url = response.data.mobile_url;
      }
      // response.checkout.payletter_respond에 있는 경우
      else if (response?.checkout?.payletter_respond?.token) {
        token = response.checkout.payletter_respond.token;
        online_url = response.checkout.payletter_respond.online_url;
        mobile_url = response.checkout.payletter_respond.mobile_url;
      }
      
      if (token) {
        // 모바일 환경 체크
        const isMobile = /Mobi|Android/i.test(navigator.userAgent);
        
        // 결제 URL 선택
        const paymentUrl = (isMobile && mobile_url) 
          ? mobile_url 
          : online_url;
        
        if (paymentUrl) {

          
          // 현재 창에서 결제 페이지로 이동
          // PayLetter에서 결제 완료 후 return_url로 자동 리다이렉트됨
          window.location.href = paymentUrl;
          return;
        } else {
          throw new Error(t('결제 URL을 받지 못했습니다.'));
        }
      } else {
        console.error(t('PayLetter API 응답:'), response);
        throw new Error(t('결제 토큰을 받지 못했습니다.'));
      }
      
    } catch (error: any) {
      console.error(t('결제 오류]'), error);
      alert(t(`결제 처리 중 오류가 발생했습니다: ${error.message || '알 수 없는 오류'}`));
    } finally {
      setIsLoading(false);
    }
  }, [
    cartItems,
    calculateTotalPrice,
    dispatch,
    formData,
    isEditor,
    selectedPgCode,
    shippingFee,
    t,
    user,
    withcookieData,
    freeShippingLimit,
    requestPayLetterPayment
  ]);
  
  // 로딩 상태 표시
  if (loading) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-loading">
                <p>{t("장바구니 정보를 불러오는 중입니다...")}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 로그인 필요
  if (!isUserLoggedIn && !isAdminLoggedIn && !isEditor) {
    return (
      <div className={className} style={style}>
        <div className="container">
          <div className="row">
            <div className="col-12">
              <div className="payment-login-required">
                <p>{t("결제를 진행하려면 로그인이 필요합니다.")}</p>
                <a href="/login" className="login-button">{t("로그인")}</a>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
  
  // 장바구니가 비어있는 경우
  if (!cartItems || cartItems.length === 0) {
    if (!isEditor) {
      return (
        <div className={className} style={style}>
          <div className="container">
            <div className="row">
              <div className="col-12">
                <div className="payment-empty">
                  <h2>{title}</h2>
                  <p>{emptyText}</p>
                  <a href="/shopping" className="continue-shopping-button">
                    {t("쇼핑 계속하기")}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      );
    }
  }
  
  const totalPrice = calculateTotalPrice();
  const appliedShippingFee = totalPrice >= freeShippingLimit ? 0 : shippingFee;
  const grandTotal = totalPrice + appliedShippingFee;
  
  return (
    <div className={className} style={style}>
      {showAddressPopup && (
        <AddressSearchPopup 
          onClose={() => setShowAddressPopup(false)} 
          initialSearchQuery={formData.address || ''}
        />
      )}
      
      <div className="container">
        <div className="row">
          <div className="col-12">
            <h2 className="payment-title">{title}</h2>
          </div>
        </div>
        
        <div className="row" style={{ margin:"0 auto" }}>
          {/* 주문 정보 및 결제 폼 */}
          <div className="col-lg-8 col-md-12">
            <div className="payment-form-wrapper">
              <h3 className="form-section-title">{t("주문자 정보")}</h3>
              <form onSubmit={handlePayment} className="payment-form">
                <div className="form-group">
                  <label htmlFor="name">{t("이름")} <span className="required">*</span></label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="phone">{t("휴대폰 번호")} <span className="required">*</span></label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder={t("010-0000-0000")}
                    className="form-control"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="memo">{t("메모")}</label>
                  <textarea
                    id="memo"
                    name="memo"
                    value={formData.memo}
                    onChange={(e) => handleInputChange(e as any)}
                    className="form-control"
                    rows={3}
                    placeholder={t("주문 시 요청사항을 입력해주세요")}
                  />
                </div>

                <h3 className="form-section-title mt-4">{t("배송지 정보")}</h3>
                
                <div className="form-group">
                  <label htmlFor="zipcode">{t("우편번호")} <span className="required">*</span></label>
                  <div className="zipcode-group">
                    <input
                      type="text"
                      id="zipcode"
                      name="zipcode"
                      value={formData.zipcode}
                      onChange={handleInputChange}
                      required
                      className="form-control"
                      readOnly
                    />
                    <button 
                      type="button" 
                      className="search-zipcode-btn"
                      onClick={handleAddressSearch}
                    >
                      {formData.address ? t("주소 변경") : t("우편번호 찾기")}
                    </button>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="address">{t("주소")} <span className="required">*</span></label>
                  <input
                    type="text"
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    className="form-control"
                    readOnly
                  />
                  {formData.address && (
                    <small className="form-text text-muted">
                      {t("주소를 변경하려면 '주소 변경' 버튼을 클릭하세요.")}
                    </small>
                  )}
                </div>
                
                <div className="form-group">
                  <label htmlFor="addressDetail">{t("상세주소")}</label>
                  <input
                    type="text"
                    id="addressDetail"
                    name="addressDetail"
                    value={formData.addressDetail}
                    onChange={handleInputChange}
                    className="form-control"
                  />
                </div>

                <h3 className="form-section-title mt-4">{t("결제 수단")}</h3>
                
                {/* PayLetter PG 코드 선택 */}
                <div className="payment-methods-container">
                  {PG_CODES.map(pg => (
                    <div key={pg.code} className={`payment-method-card ${selectedPgCode === pg.code ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        id={`pg-${pg.code}`}
                        name="pgcode"
                        value={pg.code}
                        checked={selectedPgCode === pg.code}
                        onChange={(e) => setSelectedPgCode(e.target.value)}
                        className="payment-method-input"
                      />
                      <label htmlFor={`pg-${pg.code}`} className="payment-method-label">
                        <div className="payment-method-icon">
                          {/* 각 결제 수단별 SVG 아이콘 */}
                          {pg.code === 'creditcard' && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="1" y="4" width="22" height="16" rx="2" ry="2"></rect>
                              <line x1="1" y1="10" x2="23" y2="10"></line>
                            </svg>
                          )}
                          {pg.code === 'banktransfer' && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                              <polyline points="9 22 9 12 15 12 15 22"></polyline>
                            </svg>
                          )}
                          {pg.code === 'virtualaccount' && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <line x1="8" y1="12" x2="16" y2="12"></line>
                              <line x1="12" y1="16" x2="12" y2="16"></line>
                            </svg>
                          )}
                          {pg.code === 'mobile' && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="5" y="2" width="14" height="20" rx="2" ry="2"></rect>
                              <line x1="12" y1="18" x2="12.01" y2="18"></line>
                            </svg>
                          )}
                          {(pg.code === 'kakaopay' || pg.code === 'naverpay' || pg.code === 'toss' || pg.code === 'payco' || pg.code === 'ssgpay' || pg.code === 'samsungpay' || pg.code === 'applepay') && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M21 4H3a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h18a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"></path>
                              <circle cx="12" cy="12" r="5"></circle>
                            </svg>
                          )}
                          {/* 나머지 결제 수단들은 기본 아이콘 */}
                          {!['creditcard', 'banktransfer', 'virtualaccount', 'mobile', 'kakaopay', 'naverpay', 'toss', 'payco', 'ssgpay', 'samsungpay', 'applepay'].includes(pg.code) && (
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10"></circle>
                              <path d="M16 12l-4-4-4 4M12 16V8"></path>
                            </svg>
                          )}
                        </div>
                        <div className="payment-method-info">
                          <span className="payment-method-name">{t(pg.name)}</span>
                          <span className="payment-method-desc">
                            {pg.code === 'creditcard' && t('모든 종류의 신용/체크카드')}
                            {pg.code === 'banktransfer' && t('실시간 계좌이체')}
                            {pg.code === 'virtualaccount' && t('무통장 입금')}
                            {pg.code === 'mobile' && t('휴대폰 소액결제')}
                            {pg.code === 'kakaopay' && t('카카오 간편결제')}
                            {pg.code === 'naverpay' && t('네이버 간편결제')}
                            {pg.code === 'toss' && t('토스 간편결제')}
                            {pg.code === 'payco' && t('페이코 간편결제')}
                            {pg.code === 'ssgpay' && t('SSG 간편결제')}
                            {pg.code === 'samsungpay' && t('삼성페이 간편결제')}
                            {pg.code === 'applepay' && t('애플페이 간편결제')}
                            {!['creditcard', 'banktransfer', 'virtualaccount', 'mobile', 'kakaopay', 'naverpay', 'toss', 'payco', 'ssgpay', 'samsungpay', 'applepay'].includes(pg.code) && t('기타 결제 수단')}
                          </span>
                        </div>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="terms-agreement mt-4">
                  <div className="form-check">
                    <input
                      type="checkbox"
                      id="agreeTerms"
                      name="agreeTerms"
                      checked={formData.agreeTerms}
                      onChange={handleInputChange}
                      className="form-check-input"
                      required
                    />
                    <label htmlFor="agreeTerms" className="form-check-label">
                      {t("결제 진행 및 개인정보 처리방침에 동의합니다")} <span className="required">*</span>
                    </label>
                  </div>
                </div>

                <div className="payment-button-container mt-4">
                  <button type="submit" className="payment-button" disabled={isLoading}>
                    {isLoading ? (
                      t("처리 중...")
                    ) : (
                      <>
                        {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                        <PriceDisplay price={grandTotal} /> {t("결제하기")}
                      </>
                    )}
                  </button>
                </div>
                
               
              </form>
            </div>
          </div>
          
          {/* 주문 요약 정보 */}
          <div className="col-lg-4 col-md-12">
            <div className="order-summary">
              <h3 className="summary-title">{t("주문 내역")}</h3>
              
              <div className="order-items">
                {cartItems && cartItems.length > 0 ? (
                  <ul className="order-item-list">
                    {cartItems.map((item: CartItem, index: number) => (
                      <li key={index} className="order-item">
                        <div className="order-item-image">
                          {item.product?.config?.img_url ? (
                            <img
                              src={item.product.config.img_url}
                              alt={item.product.title || '상품 이미지'}
                            />
                          ) : (
                            <div className="no-image">{t("이미지 없음")}</div>
                          )}
                        </div>
                        <div className="order-item-info">
                          <p className="item-name">
                            {item.product?.title ? t(item.product.title) : t('상품명 없음')}
                          </p>
                          
                          {/* 옵션 정보 표시 */}
                          {item.variant && item.variant.options && item.variant.options.length > 0 && (
                            <p className="item-options">
                              {item.variant.options.map((opt, index) => (
                                <span key={index}>
                                  {opt.optionValue.optionGroup.name}: {opt.optionValue.value}
                                  {index < (item.variant?.options?.length || 0) - 1 && ', '}
                                </span>
                              ))}
                            </p>
                          )}
                          
                          {/* 레거시 옵션 정보 표시 */}
                          {!item.variant && item.options && Object.keys(item.options).length > 0 && (
                            <p className="item-options">
                              {(() => {
                                const optionEntries = Object.entries(item.options)
                                  .filter(([key]) => !['sku', 'price', 'pv'].includes(key));
                                
                                if (optionEntries.length === 0) return null;
                                
                                return optionEntries.map(([key, value], index) => (
                                  <span key={key}>
                                    {key}: {value}
                                    {index < optionEntries.length - 1 && ', '}
                                  </span>
                                ));
                              })()}
                            </p>
                          )}
                          
                          <p className="item-quantity">
                            {t("수량")}: {item.count || 1}{t("개")}
                          </p>
                          <p className="item-price">
                            {(() => {
                              const basePrice = item.product?.config?.discounted_price 
                                ? Number(item.product.config.discounted_price) 
                                : item.product?.config?.default_price 
                                  ? Number(item.product.config.default_price) 
                                  : 0;
                              
                              // 직급별 가격 계산
                              const priceInfo = calculateLevelPrice(basePrice, item.product);
                              const finalBasePrice = priceInfo.levelPrice;
                              
                              const additionalPrice = item.variant?.additionalPrice 
                                ? Number(item.variant.additionalPrice) 
                                : 0;
                              
                              const totalPrice = (finalBasePrice + additionalPrice) * (item.count || 1);
                              
                              return (
                                <>
                                  {/* 직급별 가격이 있는 경우 */}
                                  {priceInfo.discount > 0 && priceInfo.levelName && (
                                    <span className="level-info-small">
                                      <span className="level-badge-tiny">{priceInfo.levelName}</span>
                                    </span>
                                  )}
                                  {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                                  <PriceDisplay price={totalPrice} />
                                  {additionalPrice > 0 && (
                                    <span className="additional-price-info">
                                      (옵션 +₩{additionalPrice.toLocaleString()})
                                    </span>
                                  )}
                                </>
                              );
                            })()}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="no-items">{t("주문 내역이 없습니다.")}</p>
                )}
              </div>
              
              <div className="order-totals">
                <div className="order-total-item">
                  <span>{t("상품 금액")}</span>
                  <span>
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={totalPrice} />
                  </span>
                </div>
                <div className="order-total-item">
                  <div className="item-label">{t("배송비")}</div>
                  <div className="item-value">
                    {totalPrice >= freeShippingLimit ? (
                      <span className="free-shipping">{t("무료배송")}</span>
                    ) : (
                      /* @ts-ignore - PriceDisplay는 price prop을 받는 컴포넌트입니다 */
                      <PriceDisplay price={appliedShippingFee} />
                    )}
                  </div>
                </div>
                <div className="order-total-item grand-total">
                  <span>{t("총 결제금액")}</span>
                  <span>
                    {/* @ts-ignore - PriceDisplay 컴포넌트 타입 무시 */}
                    <PriceDisplay price={grandTotal} />
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PayLetterPayment;

// Named export 추가
export { PayLetterPayment as PayLetterPaymentComponent }; 